import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  // Prefer the token captured by the early head-script (already stripped from the
  // URL before analytics loaded); fall back to a query param for direct navigation.
  const [token] = useState(() => (typeof window !== "undefined" && window.__RESET_TOKEN) || params.get("token") || "");
  const nav = useNavigate();
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Never record the reset page in session replay, and make sure no token is
    // left in the visible URL / history once the page has consumed it.
    try { if (window.posthog && window.posthog.stopSessionRecording) window.posthog.stopSessionRecording(); } catch (e) {}
    try {
      if (window.location.search || window.location.hash) {
        window.history.replaceState({}, document.title, "/reset-password");
      }
      if (window.__RESET_TOKEN) window.__RESET_TOKEN = undefined;
    } catch (e) {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (np.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (np !== cp) { toast.error("New password and confirmation do not match."); return; }
    setLoading(true);
    try {
      await api.resetPassword({ token, new_password: np, confirm_password: cp });
      toast.success("Password updated successfully. Sign in with your new password.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0C10] relative z-10">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "linear-gradient(120deg, rgba(10,12,16,0.4), rgba(10,12,16,0.9)), url('https://images.unsplash.com/photo-1731239027673-1a5c6cd61380?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-12 left-12 right-12">
          <div className="eyebrow text-[#D4AF37]">The Passport Experience™</div>
          <h1 className="font-display text-5xl text-[#F8FAFC] mt-3 leading-tight">
            Set a new<br />private password.
          </h1>
          <p className="text-[#94A3B8] mt-4 max-w-md">
            Choose something only you know. Signing in with your new password will end every other active session.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src="/inforno-logo.png" alt="Inforno Travel" className="h-11 w-auto" />
            <span className="font-display text-2xl">Inforno Travel</span>
          </div>

          {!token ? (
            <div data-testid="reset-invalid-panel">
              <div className="flex items-start gap-3 mb-4 p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5 shrink-0" />
                <div className="text-sm text-[#F8FAFC]">This reset link is invalid or incomplete. Please request a new one.</div>
              </div>
              <Link to="/forgot-password" data-testid="reset-request-new"
                className="gold-btn w-full py-3 rounded-lg inline-flex items-center justify-center">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <h2 className="text-xl font-semibold mb-1">Choose a new password</h2>
              <p className="text-sm text-[#94A3B8] mb-6">Enter and confirm your new private password.</p>
              <label className="block text-sm text-[#94A3B8] mb-1">New password (min 8 characters)</label>
              <input
                data-testid="reset-new-password"
                type="password" value={np} onChange={(e) => setNp(e.target.value)} required
                className="w-full mb-4 px-4 py-3 rounded-lg bg-[#12151E] border border-[#2A3042] focus:border-[#D4AF37] outline-none text-[#F8FAFC]"
              />
              <label className="block text-sm text-[#94A3B8] mb-1">Confirm new password</label>
              <input
                data-testid="reset-confirm-password"
                type="password" value={cp} onChange={(e) => setCp(e.target.value)} required
                className="w-full mb-6 px-4 py-3 rounded-lg bg-[#12151E] border border-[#2A3042] focus:border-[#D4AF37] outline-none text-[#F8FAFC]"
              />
              <button
                data-testid="reset-submit"
                type="submit" disabled={loading}
                className="gold-btn w-full py-3 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {loading ? "Updating…" : "Update password"}
              </button>
              <div className="mt-6 text-center">
                <Link to="/login" data-testid="reset-back-login"
                  className="text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
